import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, MessageSquareOff, KeyRound, Loader2, Bell } from "lucide-react";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { getRestaurant, getFeedback, changePassword, setNotificationEmail, formatApiError } from "@/api";
import { LogoutButton } from "@/components/LogoutButton";
import { useAuth } from "@/auth";
import { getBrand } from "@/brand";

const FILTERS = [
  { label: "Todas", value: 0 },
  { label: "5 estrellas", value: 5 },
  { label: "4 estrellas", value: 4 },
  { label: "3 estrellas", value: 3 },
  { label: "2 estrellas", value: 2 },
  { label: "1 estrella", value: 1 },
];

const Stars = ({ n, className = "h-3.5 w-3.5", color = "#EAB308" }) => (
  <div className="flex gap-0.5" aria-label={`${n} de 5 estrellas`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={className}
        strokeWidth={1.5}
        fill={i <= n ? color : "transparent"}
        color={i <= n ? color : "#E7E5E4"}
      />
    ))}
  </div>
);

const formatDate = (iso) =>
  new Intl.DateTimeFormat("es-ES", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));

const inputClass =
  "w-full rounded-xl border border-[#E7E2D7] bg-white/90 px-4 py-3 text-sm text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:ring-2 focus:ring-[#C25E38]/30 focus:border-[#C25E38]/50 transition-shadow duration-200";

const SummaryCard = ({ label, value, hint, testId, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay, ease: "easeOut" }}
    data-testid={testId}
    className="bg-white border border-[#E7E2D7] rounded-2xl p-5 sm:p-6 shadow-[0_10px_30px_-10px_rgba(28,25,23,0.06)]"
  >
    <p className="font-mono-label text-[10px] uppercase tracking-[0.25em] text-[#B38D46]">
      {label}
    </p>
    <p className="font-serif-display text-4xl font-medium text-[#1C1917] mt-3 tracking-tight">
      {value}
    </p>
    {hint && <p className="text-xs text-[#A8A29E] mt-1.5">{hint}</p>}
  </motion.div>
);

const MyAccount = ({ accent, slug, notificationEmail }) => {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [notifEmail, setNotifEmail] = useState(notificationEmail || "");
  const [savingNotif, setSavingNotif] = useState(false);

  const handleSaveNotif = async (e) => {
    e.preventDefault();
    if (savingNotif) return;
    setSavingNotif(true);
    try {
      await setNotificationEmail(slug, notifEmail.trim());
      toast.success("Email de notificaciones guardado.");
    } catch (err) {
      toast.error(formatApiError(err, "No se ha podido guardar el email."));
    } finally {
      setSavingNotif(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (newPassword !== confirmPassword) {
      toast.error("Las contraseñas no coinciden.");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("La nueva contraseña debe tener al menos 8 caracteres.");
      return;
    }
    setSaving(true);
    try {
      await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      toast.success("Contraseña actualizada correctamente.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(formatApiError(err, "No se ha podido cambiar la contraseña."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.34, ease: "easeOut" }}
      data-testid="my-account-section"
      className="bg-white border border-[#E7E2D7] rounded-2xl p-5 sm:p-7 shadow-[0_10px_30px_-10px_rgba(28,25,23,0.06)] flex flex-col gap-5"
    >
      <div className="flex items-center gap-2.5">
        <KeyRound className="h-4 w-4" style={{ color: accent }} />
        <h2 className="font-serif-display text-xl sm:text-2xl font-medium text-[#1C1917]">
          Mi cuenta
        </h2>
      </div>

      <p className="text-sm text-[#57534E]">
        Email de acceso:{" "}
        <span data-testid="account-email" className="font-mono-label text-[#1C1917]">
          {user?.email}
        </span>
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-testid="change-password-form">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="input-current-password" className="font-mono-label text-[10px] uppercase tracking-[0.25em] text-[#A8A29E] px-1">
              Contraseña actual
            </label>
            <input
              id="input-current-password"
              data-testid="input-current-password"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className={inputClass}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="input-new-password" className="font-mono-label text-[10px] uppercase tracking-[0.25em] text-[#A8A29E] px-1">
              Nueva contraseña
            </label>
            <input
              id="input-new-password"
              data-testid="input-new-password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              className={inputClass}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="input-confirm-password" className="font-mono-label text-[10px] uppercase tracking-[0.25em] text-[#A8A29E] px-1">
              Confirmar nueva contraseña
            </label>
            <input
              id="input-confirm-password"
              data-testid="input-confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className={inputClass}
            />
          </div>
        </div>
        <div>
          <button
            type="submit"
            data-testid="change-password-button"
            disabled={saving}
            className="flex items-center gap-2 text-[#FDFBF7] font-medium text-sm rounded-xl px-5 py-3 transition-colors duration-300 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ backgroundColor: accent }}
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Cambiar contraseña
          </button>
        </div>
      </form>

      <div className="border-t border-[#E7E2D7] pt-5 flex flex-col gap-4" data-testid="notifications-section">
        <div className="flex items-center gap-2.5">
          <Bell className="h-4 w-4" style={{ color: accent }} />
          <h3 className="font-serif-display text-lg font-medium text-[#1C1917]">
            Notificaciones
          </h3>
        </div>
        <p className="text-xs text-[#A8A29E] leading-relaxed">
          Recibirás un aviso por email con cada nueva valoración. Es
          independiente de tu email de acceso.
        </p>
        <form onSubmit={handleSaveNotif} className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            data-testid="input-notification-email"
            value={notifEmail}
            onChange={(e) => setNotifEmail(e.target.value)}
            placeholder="encargado@turestaurante.com"
            required
            className={inputClass}
          />
          <button
            type="submit"
            data-testid="save-notification-email-button"
            disabled={savingNotif}
            className="flex items-center justify-center gap-2 text-[#FDFBF7] font-medium text-sm rounded-xl px-5 py-3 transition-colors duration-300 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
            style={{ backgroundColor: accent }}
          >
            {savingNotif && <Loader2 className="h-4 w-4 animate-spin" />}
            Guardar
          </button>
        </form>
      </div>
    </motion.section>
  );
};

export default function AdminDashboard() {
  const { slug } = useParams();
  const [restaurant, setRestaurant] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [filter, setFilter] = useState(0);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    Promise.all([getRestaurant(slug), getFeedback(slug)])
      .then(([r, f]) => {
        setRestaurant(r);
        setReviews(f);
      })
      .catch(() => {
        setNotFound(true);
        toast.error("No se han podido cargar las opiniones.");
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const stats = useMemo(() => {
    const total = reviews.length;
    const avg = total
      ? reviews.reduce((acc, r) => acc + r.rating, 0) / total
      : 0;
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const week = reviews.filter(
      (r) => new Date(r.created_at).getTime() >= weekAgo,
    ).length;
    return { total, avg, week };
  }, [reviews]);

  const chartData = useMemo(() => {
    const days = [];
    const now = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      const label = `${new Intl.DateTimeFormat("es-ES", { weekday: "short" })
        .format(d)
        .replace(".", "")} ${d.getDate()}`;
      days.push({ key: d.toDateString(), label, count: 0 });
    }
    const byDay = Object.fromEntries(days.map((d) => [d.key, d]));
    reviews.forEach((r) => {
      const k = new Date(r.created_at).toDateString();
      if (byDay[k]) byDay[k].count += 1;
    });
    return days;
  }, [reviews]);

  const filtered = useMemo(
    () => (filter === 0 ? reviews : reviews.filter((r) => r.rating === filter)),
    [reviews, filter],
  );

  if (notFound) {
    return (
      <div className="relative min-h-screen bg-[#FDFBF7] text-[#1C1917] flex items-center justify-center px-5">
        <div className="grain-overlay" />
        <div className="text-center space-y-3" data-testid="admin-not-found">
          <h1 className="font-serif-display text-3xl font-medium tracking-tight">
            Restaurante no encontrado
          </h1>
          <p className="text-sm text-[#57534E]">
            Comprueba que la dirección del panel es correcta.
          </p>
        </div>
      </div>
    );
  }

  const brand = getBrand(restaurant);

  return (
    <div className="relative min-h-screen bg-[#FDFBF7] text-[#1C1917]">
      <div className="ambient-glow" />
      <div className="grain-overlay" />

      <div
        className="relative z-10 max-w-4xl mx-auto px-5 py-8 sm:py-12 flex flex-col gap-10"
        data-testid="admin-dashboard"
      >
        <motion.header
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="space-y-4"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3.5 min-w-0">
              {brand.logoUrl ? (
                <img
                  src={brand.logoUrl}
                  alt={`Logo de ${brand.name}`}
                  data-testid="admin-brand-logo"
                  className="h-12 w-12 rounded-xl object-cover border border-[#E7E2D7] bg-white shadow-[0_10px_30px_-10px_rgba(28,25,23,0.12)]"
                />
              ) : (
                <div className="h-12 w-12 rounded-xl border border-[#E7E2D7] bg-white flex items-center justify-center shrink-0">
                  <Star className="h-5 w-5" fill={brand.accent} color={brand.accent} />
                </div>
              )}
              <p
                data-testid="admin-restaurant-name"
                className="font-mono-label text-[10px] uppercase tracking-[0.3em] text-[#B38D46] leading-relaxed"
              >
                {restaurant ? `Restaurante ${restaurant.name}` : "Cargando restaurante..."}
              </p>
            </div>
            <LogoutButton />
          </div>
          <h1
            data-testid="admin-heading"
            className="font-serif-display text-4xl sm:text-5xl font-medium tracking-tight leading-[1.1]"
          >
            Panel de <em style={{ color: brand.accent }}>opiniones</em>
          </h1>
        </motion.header>

        {loading ? (
          <p
            data-testid="admin-loading"
            className="text-sm text-[#A8A29E] font-mono-label uppercase tracking-[0.25em] animate-pulse"
          >
            Cargando opiniones...
          </p>
        ) : (
          <>
            <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <SummaryCard
                label="Valoración media"
                value={stats.avg.toLocaleString("es-ES", {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })}
                hint="sobre 5 estrellas"
                testId="summary-card-average"
                delay={0.05}
              />
              <SummaryCard
                label="Opiniones recibidas"
                value={stats.total}
                hint="en total"
                testId="summary-card-total"
                delay={0.12}
              />
              <SummaryCard
                label="Esta semana"
                value={stats.week}
                hint="últimos 7 días"
                testId="summary-card-week"
                delay={0.19}
              />
            </section>

            <motion.section
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.24, ease: "easeOut" }}
              className="bg-white border border-[#E7E2D7] rounded-2xl p-5 sm:p-7 shadow-[0_10px_30px_-10px_rgba(28,25,23,0.06)]"
            >
              <h2 className="font-serif-display text-xl sm:text-2xl font-medium text-[#1C1917] mb-6">
                Evolución
              </h2>
              <div className="h-52 w-full" data-testid="evolution-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: "#A8A29E" }}
                      axisLine={{ stroke: "#E7E2D7" }}
                      tickLine={false}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 11, fill: "#A8A29E" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(28,25,23,0.04)" }}
                      contentStyle={{
                        borderRadius: 12,
                        border: "1px solid #E7E2D7",
                        fontSize: 12,
                        color: "#1C1917",
                      }}
                      formatter={(v) => [v, "Opiniones"]}
                      labelFormatter={(l) => `Día ${l}`}
                    />
                    <Bar dataKey="count" fill={brand.accent} radius={[6, 6, 0, 0]} maxBarSize={42} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </motion.section>

            <motion.section
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3, ease: "easeOut" }}
              className="flex flex-col gap-5"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="font-serif-display text-xl sm:text-2xl font-medium text-[#1C1917]">
                  Últimas opiniones
                </h2>
                <div className="flex flex-wrap gap-1.5" data-testid="review-filters">
                  {FILTERS.map((f) => {
                    const active = filter === f.value;
                    return (
                      <button
                        key={f.value}
                        type="button"
                        data-testid={f.value === 0 ? "filter-all" : `filter-${f.value}`}
                        onClick={() => setFilter(f.value)}
                        style={
                          active
                            ? { backgroundColor: brand.accent, borderColor: brand.accent, color: "#FDFBF7" }
                            : undefined
                        }
                        className={`text-xs font-medium rounded-full px-3 py-1.5 border transition-colors duration-200 cursor-pointer ${
                          active
                            ? ""
                            : "bg-white/70 border-[#E7E2D7] text-[#57534E] hover:border-[#C25E38]/40"
                        }`}
                      >
                        {f.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {filtered.length === 0 ? (
                <div
                  data-testid="reviews-empty"
                  className="bg-white/60 border border-dashed border-[#E7E2D7] rounded-2xl p-10 flex flex-col items-center gap-3 text-center"
                >
                  <MessageSquareOff className="h-6 w-6 text-[#A8A29E]" />
                  <p className="text-sm text-[#A8A29E]">
                    No hay opiniones con este filtro.
                  </p>
                </div>
              ) : (
                <ul className="flex flex-col gap-3" data-testid="reviews-list">
                  {filtered.map((r, i) => (
                    <motion.li
                      key={r.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: Math.min(i * 0.04, 0.4) }}
                      data-testid={`review-item-${r.id}`}
                      className="bg-white border border-[#E7E2D7] rounded-2xl px-5 py-4 shadow-[0_10px_30px_-10px_rgba(28,25,23,0.06)] flex flex-col gap-2"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <Stars n={r.rating} color={brand.star} />
                        <span className="font-mono-label text-[10px] uppercase tracking-[0.15em] text-[#A8A29E] whitespace-nowrap">
                          {formatDate(r.created_at)}
                        </span>
                      </div>
                      {r.feedback ? (
                        <p className="text-sm text-[#57534E] leading-relaxed">
                          {r.feedback}
                        </p>
                      ) : (
                        <p className="text-sm text-[#A8A29E] italic">
                          Sin comentario
                        </p>
                      )}
                    </motion.li>
                  ))}
                </ul>
              )}
            </motion.section>

            <MyAccount accent={brand.accent} slug={slug} notificationEmail={restaurant?.notification_email} />
          </>
        )}

        <footer className="text-center pb-2">
          <p className="font-mono-label text-[9px] uppercase tracking-[0.25em] text-[#A8A29E]">
            Review QR · Panel interno
          </p>
        </footer>
      </div>
    </div>
  );
}
